import { memo, useCallback } from 'react'

import {
  ShowcaseCollectionBackgroundModeEnum,
  ShowcaseCollectionEditBackgroundModeEnum,
} from '@nativewaves/nw-cloud-sdk/showcase'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/Showcases/Collections'
import { useCollectionUpdateMutation } from 'hooks/mutations/showcase'
import {
  accessKeysOverviewRoute,
  configRoute,
  rawRoute,
  collectionRoute,
  showcasesOverviewRoute,
} from 'pages/NWPlatform/Collections/Collection'
import { collectionQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { collectionId } = collectionRoute.useParams()

  const collectionQuery = useQuery(collectionQueryKeys.detail({ collectionId }))
  const { data: collection } = collectionQuery

  const { mutate: updateCollection, ...collectionUpdateMutation } = useCollectionUpdateMutation()

  const handleUpdateCollectionName = useCallback(
    (title: string, closeCallback: () => void) => {
      if (collection) {
        updateCollection(
          {
            collectionId,
            showcaseCollectionEdit: {
              backgroundTopGradientHexColor: collection.backgroundTopGradientHexColor,
              backgroundMode:
                collection.backgroundMode === ShowcaseCollectionBackgroundModeEnum.None
                  ? ShowcaseCollectionEditBackgroundModeEnum.None
                  : ShowcaseCollectionEditBackgroundModeEnum.TopGradient,
              showWideThumbnail: collection.showWideThumbnail,
              name: title,
            },
          },
          { onError: () => enqueueSnackbar(t('pages:Showcase.updateError')), onSettled: closeCallback },
        )
      }
    },
    [t, collection, collectionId, enqueueSnackbar, updateCollection],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={collectionQuery.data?.name ?? ''}
          isLoading={collectionQuery.isLoading}
          isSaving={collectionUpdateMutation.isPending}
          onSave={handleUpdateCollectionName}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={collectionRoute.to} to={configRoute.to}>
                {t('pages:Showcase.config')}
              </NavigationTabLink>
              <NavigationTabLink from={collectionRoute.to} to={showcasesOverviewRoute.to}>
                {t('pages:Showcase.showcases')}
              </NavigationTabLink>
              <NavigationTabLink from={collectionRoute.to} to={accessKeysOverviewRoute.to}>
                {t('pages:Showcase.accessKeys')}
              </NavigationTabLink>
              <NavigationTabLink from={collectionRoute.to} to={rawRoute.to}>
                {t('pages:Showcase.json')}
              </NavigationTabLink>
            </Tabs>
          }
          Actions={<ActionsMenu />}
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
